import { analytics } from 'libs';
import { Select } from 'components/common';
import Toggle from './Toggle';
import Filter from './Filter';

interface Filters {
  purpose?: string;
  programs?: string[];
}

interface PurposeOption {
  label: string;
  value: string;
}

interface RateTypeOption {
  key: string;
  value: string;
  filter: string;
}

interface PurposeAndRateTypeFilterProps {
  isPro?: boolean;
  filters: Filters;
  onFilter: (update: { purpose?: string | null; programs?: string[] | null }) => void;
}

const PurposeAndRateTypeFilter = ({ isPro = false, filters, onFilter }: PurposeAndRateTypeFilterProps) => {
  const options: { purpose: PurposeOption[]; rateType: RateTypeOption[] } = {
    purpose: [
      {
        label: 'Owner Occupied',
        value: 'Owner Occupied',
      },
      {
        label: 'Rental: 1 Unit',
        value: 'Rental: 1 Unit',
      },
      {
        label: 'Rental: 2-4 Units',
        value: 'Rental: 2-4 Units',
      },
    ],
    rateType: [
      { key: 'standard', value: 'Standard', filter: 'Standard Products' },
      { key: 'limited', value: 'Limited', filter: 'Limited Feature' },
      { key: 'promo', value: 'Promo', filter: 'Promotions' },
    ],
  };

  const onPurposeChange = (selected: PurposeOption | null) => {
    const { programs = [] } = filters;
    const purpose = selected?.value || null;

    analytics.productSearch.filter({ purpose: purpose || 'cleared' });

    const rateTypeFilters = options.rateType.map((option) => option.filter);
    const shouldRemoveRateTypeFilters = !isRateTypeAllowed(purpose);
    const updatedPrograms = shouldRemoveRateTypeFilters
      ? programs.filter((program) => !rateTypeFilters.includes(program))
      : programs;

    onFilter({
      purpose,
      ...(shouldRemoveRateTypeFilters && { programs: updatedPrograms }),
    });
  };

  const onRateTypeChange = (key: string) => {
    const { rateType: rateTypeOptions } = options;
    const currentPrograms = filters.programs || [];

    const pressed = rateTypeOptions.find((option) => option.key === key)!;
    const rateTypeFilters = rateTypeOptions.map((option) => option.filter);

    const isSelected = currentPrograms.includes(pressed.filter);

    const nonRateTypePrograms = currentPrograms.filter((program) => !rateTypeFilters.includes(program));

    const nextPrograms = isSelected ? nonRateTypePrograms : [...nonRateTypePrograms, pressed.filter];

    analytics.productSearch.filter({
      rate_type: isSelected ? 'cleared' : pressed.filter,
    });

    onFilter({ programs: nextPrograms.length ? nextPrograms : null });
  };

  return (
    <>
      <Filter name="Purpose">
        <Select
          className="form-react-select"
          clearValue={null}
          onChange={onPurposeChange}
          options={options.purpose}
          placeholder="All"
          selected={options.purpose.find((option) => option.value === filters.purpose) || null}
          testId="filter-purpose"
        />
      </Filter>
      <RateTypeToggle
        disabled={!isRateTypeAllowed(filters.purpose)}
        filters={filters}
        isPro={isPro}
        onChange={onRateTypeChange}
        options={options.rateType}
      />
    </>
  );
};

interface RateTypeToggleProps {
  disabled?: boolean;
  filters: { programs?: string[] };
  isPro?: boolean;
  onChange: (key: string) => void;
  options: RateTypeOption[];
}

const RateTypeToggle = ({
  disabled = false,
  filters: { programs = [] },
  isPro = false,
  onChange,
  options,
}: RateTypeToggleProps) => {
  const selectedOptions = options.filter((option) => programs.includes(option.filter));
  const value = selectedOptions.map((option) => option.key);

  return (
    <Filter
      name="Rate Type"
      isPro={isPro}
      tooltip={
        <span>
          Narrow your search for owner occupied rates.
          <br />
          <a
            href="https://support.lenderspotlight.ca/en/articles/4939832-rate-type-definitions"
            rel="noopener noreferrer"
            target="_blank"
          >
            Click for definitions.
          </a>
        </span>
      }
    >
      <Toggle
        isPro={isPro}
        onChange={onChange}
        options={options}
        testId="filter-rate-type"
        value={value}
        disabled={disabled}
      />
    </Filter>
  );
};

const isRateTypeAllowed = (purpose: string | null | undefined) =>
  [null, undefined, 'Owner Occupied'].includes(purpose as any);

export default PurposeAndRateTypeFilter;
